import type { Node } from '../types';

/**
 * Rehype plugin to process Mermaid diagram code blocks
 * Converts ```mermaid code blocks into renderable Mermaid divs
 *
 * @example
 * ```mermaid
 * graph TD
 *   A[Start] --> B[Process]
 * ```
 *
 * Becomes:
 * <div class="mermaid">graph TD\n  A[Start] --> B[Process]</div>
 */
export function rehypeMermaid() {
  return (tree: Node) => {
    console.log('[rehypeMermaid] Plugin invoked, starting tree traversal');
    let mermaidBlocksFound = 0;
    let mermaidBlocksTransformed = 0;

    visit(tree, 'element', (node: any) => {
      // Look for <pre><code class="language-mermaid"> blocks
      if (node.tagName === 'pre' && node.children && node.children[0]?.tagName === 'code') {
        const codeNode = node.children[0];
        const className = codeNode.properties?.className || [];
        console.log(`[rehypeMermaid] Found code block with classes: ${JSON.stringify(className)}`);

        // Check if it's a Mermaid code block
        if (Array.isArray(className) && className.includes('language-mermaid')) {
          mermaidBlocksFound++;
          console.log(`[rehypeMermaid] Found Mermaid block #${mermaidBlocksFound}`);

          // Extract the diagram code
          let diagramCode = extractTextContent(codeNode);
          console.log(`[rehypeMermaid] Diagram code length: ${diagramCode.length} chars`);

          // Enterprise-grade HTML entity decoding (comprehensive fix)
          diagramCode = diagramCode
            // Standard HTML entities
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&apos;/g, "'")
            // Hexadecimal entities
            .replace(/&#x3C;/g, '<')
            .replace(/&#x3E;/g, '>')
            .replace(/&#x26;/g, '&')
            .replace(/&#x22;/g, '"')
            // Decimal entities
            .replace(/&#60;/g, '<')
            .replace(/&#62;/g, '>')
            .replace(/&#38;/g, '&')
            .replace(/&#34;/g, '"')
            // Corrupted entity patterns (from your error)
            .replace(/&ﬂ°x3C¶ß/g, '<')
            .replace(/&ﬂ°x3E¶ß/g, '>')
            .replace(/&ﬂ°x7C¶ß/g, '|')
            // Other corrupted patterns we might encounter
            .replace(/&[a-zA-Z0-9°¶ß]*x3C[a-zA-Z0-9°¶ß]*/g, '<')
            .replace(/&[a-zA-Z0-9°¶ß]*x3E[a-zA-Z0-9°¶ß]*/g, '>')
            // Additional safety patterns
            .replace(/&#[xX]([0-9A-Fa-f]+);/g, (_, hex) => {
              const code = parseInt(hex, 16);
              return String.fromCharCode(code);
            })
            .replace(/&#([0-9]+);/g, (_, dec) => {
              return String.fromCharCode(parseInt(dec, 10));
            });

          console.log(`[rehypeMermaid] Fixed HTML entities in diagram code`);

          // Replace the <pre><code> with <div class="mermaid">
          node.tagName = 'div';
          node.properties = {
            className: ['mermaid'],
            'data-processed': 'false',
          };
          node.children = [
            {
              type: 'text',
              value: diagramCode,
            },
          ];

          mermaidBlocksTransformed++;
          console.log(`[rehypeMermaid] Transformed to <div class="mermaid">`);
        }
      }
    });

    console.log(
      `[rehypeMermaid] Transformation complete: ${mermaidBlocksTransformed}/${mermaidBlocksFound} blocks transformed`
    );
  };
}

/**
 * Visit all nodes in the tree
 * Simple recursive tree walker
 */
function visit(node: Node, type: string, visitor: (node: any) => void): void {
  if (node.type === type) {
    visitor(node);
  }

  if (node.children) {
    for (const child of node.children) {
      visit(child, type, visitor);
    }
  }
}

/**
 * Extract text content from a node and its children
 */
function extractTextContent(node: any): string {
  if (node.type === 'text') {
    return node.value || '';
  }

  if (node.children) {
    return node.children.map((child: any) => extractTextContent(child)).join('');
  }

  return '';
}

/**
 * Get the Mermaid initialization script
 * Returns script tag with Mermaid CDN and initialization
 */
export function getMermaidScript(): string {
  return `
<!-- Mermaid Diagram Rendering -->
<script type="module">
  import mermaid from 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs';

  mermaid.initialize({
    startOnLoad: true,
    theme: 'default',
    securityLevel: 'loose',
    fontFamily: 'monospace',
    logLevel: 'error'
  });

  // Process diagrams after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      mermaid.run({ querySelector: '.mermaid' });
    });
  } else {
    mermaid.run({ querySelector: '.mermaid' });
  }
</script>
`;
}

/**
 * Get inline Mermaid CSS for styling
 */
export function getMermaidCSS(): string {
  return `
<style>
/* Professional Mermaid Diagram Styling */
.mermaid {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  margin: 2rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 2px solid #cbd5e1;
  border-radius: 12px;
  min-height: 150px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.mermaid svg {
  max-width: 100%;
  height: auto;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

/* Professional color scheme */
.mermaid .node rect,
.mermaid .node polygon,
.mermaid .node circle {
  stroke: #475569 !important;
  stroke-width: 2px !important;
  fill: #f1f5f9 !important;
}

.mermaid .node text {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  fill: #1e293b !important;
}

.mermaid .edgePath path {
  stroke: #64748b !important;
  stroke-width: 2px !important;
}

.mermaid .edgeLabel {
  background-color: white !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 4px !important;
  padding: 4px 8px !important;
  font-family: 'SF Pro Text', -apple-system, sans-serif !important;
  font-size: 12px !important;
  color: #374151 !important;
}

/* Ensure diagrams are visible in print */
@media print {
  .mermaid {
    background: white !important;
    border: 2px solid #94a3b8 !important;
    page-break-inside: avoid;
    break-inside: avoid;
    box-shadow: none !important;
  }

  .mermaid svg {
    filter: none !important;
  }
}
</style>
`;
}
